import React, { FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { UseFormReturn } from "react-hook-form";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import {
  CreateAssessmentDto,
  UpdateAssessmentDto,
  type AssessmentDto,
} from "@green0meter/api-client";

import { AssessmentFormValues } from "../../AssessmentEditor.types";

import { useCreateAssessment, useUpdateAssessment, useDeleteAssessment } from "@api";
import { Stack, Button, Typography } from "@components/base";
import { useUserStore } from "@store/user";

interface AssessmentEditorActionsProps {
  handleClose: () => void;
  form: UseFormReturn<AssessmentFormValues>;
  assessment: AssessmentDto | null;
  onDeleteSuccess?: () => void;
  onUpdateSuccess?: () => void;
  onCreateSuccess?: () => void;
  onPendingChange?: (isPending: boolean) => void;
}

const AssessmentEditorActions: FC<AssessmentEditorActionsProps> = ({
  handleClose,
  form,
  assessment,
  onCreateSuccess,
  onUpdateSuccess,
  onDeleteSuccess,
  onPendingChange,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const isCreateMode = !assessment;

  const {
    handleSubmit,
    formState: { isValid, isDirty },
  } = form;

  const { createAssessmentAsync, isPendingCreateAssessment } =
    useCreateAssessment(user);
  const { updateAssessmentAsync, isPendingUpdateAssessment } =
    useUpdateAssessment(user);
  const { deleteAssessmentAsync, isPendingDeleteAssessment } =
    useDeleteAssessment(user);

  const isPending =
    isPendingCreateAssessment ||
    isPendingUpdateAssessment ||
    isPendingDeleteAssessment;

  useEffect(() => {
    onPendingChange?.(isPending);
  }, [isPending, onPendingChange]);

  const onSubmit = async (data: AssessmentFormValues): Promise<void> => {
    const attachmentIds =
      data.attachments?.map((file) => file.fileId ?? file.pathName) ?? [];

    try {
      if (isCreateMode) {
        const payload: CreateAssessmentDto = {
          ...(data as unknown as CreateAssessmentDto),
          attachments: attachmentIds,
        };

        await createAssessmentAsync(payload);
        onCreateSuccess?.();
        handleClose();
      } else if (assessment) {
        const payload: UpdateAssessmentDto = {
          ...(data as unknown as UpdateAssessmentDto),
          attachments: attachmentIds,
        };

        await updateAssessmentAsync({
          id: assessment.id,
          data: payload,
        });
        onUpdateSuccess?.();
        handleClose();
      }
    } catch {
      // Error handling is managed by the hooks via snackbar notifications
    }
  };

  const handleDeleteClick = (): void => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!assessment) return;

    try {
      await deleteAssessmentAsync({ id: assessment.id, deleteQuestions: true });
      onDeleteSuccess?.();
      setShowDeleteDialog(false);
      handleClose();
    } catch {
      // Error handling is managed by the hooks via snackbar notifications
      setShowDeleteDialog(false);
    }
  };

  const handleDeleteCancel = (): void => {
    setShowDeleteDialog(false);
  };

  return (
    <>
      <Stack direction="row" spacing={2} justifyContent="end">
        {!isCreateMode && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            disabled={isPending}
          >
            {t("common.remove")}
          </Button>
        )}
        <Stack
          direction="row"
          spacing={2}
          sx={{ marginLeft: isCreateMode ? "auto" : undefined }}
        >
          <Button variant="outlined" onClick={handleClose} disabled={isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || !isDirty || isPending}
            isLoading={isPendingCreateAssessment || isPendingUpdateAssessment}
          >
            {isCreateMode ? t("common.add", "Add") : t("common.save")}
          </Button>
        </Stack>
      </Stack>

      <Dialog open={showDeleteDialog} onClose={handleDeleteCancel}>
        <DialogTitle>
          {t("assessments.deleteDialog.title", "Confirm delete")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              "assessments.deleteDialog.message",
              "Are you sure you want to remove this assessment? This action cannot be undone.",
            )}
          </Typography>
          {assessment && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>{assessment.assessmentId}</strong>:{" "}
              {assessment.sustainabilityMatter}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isPendingDeleteAssessment}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isPendingDeleteAssessment}
            isLoading={isPendingDeleteAssessment}
          >
            {t("common.remove")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssessmentEditorActions;
